import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox, Form } from '@arco-design/web-react';

import { BindingResourceStep } from '@/components-biz/ModelSubscriptionForm';

import { buildPresetStringListKeySet } from '../../platform/access-policy/orderListItemLock';
import { useOptionalResourceFormContext } from '../../platform/resource-form';
import type { ModelSubscriptionOtherBlockProps } from './types';

function isOrderResourceEditMode(mode?: string) {
  return mode === 'order-fixed' || mode === 'order-custom' || mode === 'order-post-paid';
}

/** 模型订阅高级设置；下单时请求/输入/输出速率只读，有数据才展示 */
export function ModelSubscriptionOtherBlock({ form, paths, options, ...control }: ModelSubscriptionOtherBlockProps) {
  const { t } = useTranslation();
  const isPreview = control.blockMode === 'preview';
  const isOrder = isOrderResourceEditMode(options?.resourceEditMode);

  const showRequests = Boolean(Form.useWatch(paths.showRequests, form));
  const showInputTokens = Boolean(Form.useWatch(paths.showInputTokens, form));
  const showOutputTokens = Boolean(Form.useWatch(paths.showOutputTokens, form));

  const defaultRules = useMemo(
    () => [
      {
        required: true,
        message: t('common:required.field'),
      },
    ],
    [t],
  );

  const clearFields = useCallback(
    (fieldPaths: string[]) => {
      const patch = Object.fromEntries(fieldPaths.map((path) => [path, undefined]));
      form.setFieldsValue(patch);
    },
    [form],
  );

  const onToggleRequests = useCallback(
    (checked: boolean) => {
      if (isPreview || isOrder) return;
      form.setFieldValue(paths.showRequests, checked);
      if (!checked) {
        clearFields([paths.requests]);
      }
    },
    [clearFields, form, isOrder, isPreview, paths.requests, paths.showRequests],
  );

  const onToggleInputTokens = useCallback(
    (checked: boolean) => {
      if (isPreview || isOrder) return;
      form.setFieldValue(paths.showInputTokens, checked);
      if (!checked) {
        clearFields([paths.inputTokensValue, paths.inputTokensWindowSeconds]);
      }
    },
    [clearFields, form, isOrder, isPreview, paths.inputTokensValue, paths.inputTokensWindowSeconds, paths.showInputTokens],
  );

  const onToggleOutputTokens = useCallback(
    (checked: boolean) => {
      if (isPreview || isOrder) return;
      form.setFieldValue(paths.showOutputTokens, checked);
      if (!checked) {
        clearFields([paths.outputTokensValue, paths.outputTokensWindowSeconds]);
      }
    },
    [clearFields, form, isOrder, isPreview, paths.outputTokensValue, paths.outputTokensWindowSeconds, paths.showOutputTokens],
  );

  const rateLimitsLocked = isPreview || isOrder;
  const resourceFormCtx = useOptionalResourceFormContext();
  const stringListPresetLocks = useMemo(() => {
    if (!isOrder) return undefined;
    const preset = resourceFormCtx?.presetConfigSnapshot;
    return {
      allowedClientCIDRs: buildPresetStringListKeySet(preset, 'allowedClientCIDRs'),
      allowedClientIPs: buildPresetStringListKeySet(preset, 'allowedClientIPs'),
    };
  }, [isOrder, resourceFormCtx?.presetConfigSnapshot]);

  const resourceOptions = useMemo(
    () => ({
      showProviderSelector: false,
      hideTokenLimitCard: true,
      hideBillingTotalTokens: true,
      requestsReadOnly: rateLimitsLocked,
      inputRateReadOnly: rateLimitsLocked,
      outputRateReadOnly: rateLimitsLocked,
      limitTogglesReadOnly: rateLimitsLocked,
      requestsToggleReadOnly: rateLimitsLocked,
      inputRateToggleReadOnly: rateLimitsLocked,
      outputRateToggleReadOnly: rateLimitsLocked,
      /** 下单：无配置数据则不展示对应卡片 */
      hideRequestsCard: isOrder && !showRequests,
      hideInputRateCard: isOrder && !showInputTokens,
      hideOutputRateCard: isOrder && !showOutputTokens,
      showRequests,
      showInputTokens,
      showOutputTokens,
      onToggleRequests,
      onToggleInputTokens,
      onToggleOutputTokens,
      stringListPresetLocks,
    }),
    [
      isOrder,
      onToggleInputTokens,
      onToggleOutputTokens,
      onToggleRequests,
      rateLimitsLocked,
      showInputTokens,
      showOutputTokens,
      showRequests,
      stringListPresetLocks,
    ],
  );

  return (
    <>
      {/* 注册限额开关字段，避免多步骤表单切换后 getFieldsValue 丢失开关状态 */}
      <Form.Item field={paths.showRequests} triggerPropName="checked" hidden>
        <Checkbox />
      </Form.Item>
      <Form.Item field={paths.showInputTokens} triggerPropName="checked" hidden>
        <Checkbox />
      </Form.Item>
      <Form.Item field={paths.showOutputTokens} triggerPropName="checked" hidden>
        <Checkbox />
      </Form.Item>
      <BindingResourceStep form={form} defaultRules={defaultRules} options={resourceOptions} />
    </>
  );
}
